import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import Body from 'src/App/Dashboards/Market/Components/Text/Body';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { COLOR_VERIFIED_TICK } from 'src/utils/const';
import {
  MARKET_WIDE_BADGE_TOOLTIP,
  VERIFIED_LOCK_BADGE_TOOLTIP,
} from './overviewSectionBadgeTooltips';

const BADGE_PILL_SX = {
  display: 'inline-flex' as const,
  alignItems: 'center',
  gap: 0.375,
  px: 1.25,
  py: 0.375,
  borderRadius: 10,
  fontSize: '0.75rem',
  fontWeight: 600,
  cursor: 'help' as const,
};

export interface IntelligenceSectionProps {
  title: string;
  /** Optional node rendered next to the title (e.g. Airbnb badge). */
  titleAdornment?: React.ReactNode;
  summary?: string | string[];
  isLoading?: boolean;
  isMobileView?: boolean;
  handleOnSampleClick?: (plan: string) => void;
  children?: React.ReactNode;
  showMarketWideBadge?: boolean;
  showVerifiedLockBadge?: boolean;
  filterApplied?: boolean;
  isVerifiedMarket?: boolean;
}

const IntelligenceSection: React.FC<IntelligenceSectionProps> = ({
  title,
  titleAdornment,
  summary,
  isLoading = false,
  isMobileView = false,
  handleOnSampleClick = () => {},
  children,
  showMarketWideBadge = false,
  showVerifiedLockBadge = false,
  filterApplied = false,
  isVerifiedMarket = true,
}) => {
  const summaryParagraphs = Array.isArray(summary) ? summary : summary ? [summary] : [];
  const showMarketWide = showMarketWideBadge && filterApplied;
  const showLock = showVerifiedLockBadge && !isVerifiedMarket;

  return (
    <Box className="section--spacing" sx={{ width: '100%' }}>
      <Box
        className="subsection--spacing"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h1" className="marketTitle" sx={{ display: 'block' }}>
          {title}
        </Typography>
        {titleAdornment}
        {showMarketWide && (
          <Tooltip title={MARKET_WIDE_BADGE_TOOLTIP} arrow placement="top">
            <Box component="span" sx={{ ...BADGE_PILL_SX, backgroundColor: COLOR_PRIMARY_2, color: '#fff' }}>
              <InfoOutlinedIcon sx={{ fontSize: '0.875rem' }} />
              Filter not applied
            </Box>
          </Tooltip>
        )}
        {showLock && (
          <Tooltip title={VERIFIED_LOCK_BADGE_TOOLTIP} arrow placement="top">
            <Box component="span" sx={{ ...BADGE_PILL_SX, backgroundColor: COLOR_VERIFIED_TICK, color: '#fff' }}>
              <LockIcon sx={{ fontSize: '0.875rem' }} />
              Verified Markets Only
            </Box>
          </Tooltip>
        )}
      </Box>
      {summaryParagraphs.length > 0 && (
        <Box className="subsection--spacing">
          <AIOverviewCard isLoading={isLoading} isMobileView={isMobileView}>
            {summaryParagraphs.map((paragraph, index) => (
              <Body
                key={index}
                handleOnSampleClick={handleOnSampleClick}
                sx={index > 0 ? { mt: 1, mb: -1 } : undefined}
              >
                {paragraph}
              </Body>
            ))}
          </AIOverviewCard>
        </Box>
      )}
      {children && (
        <Box className="subsection--spacing" sx={{ width: '100%' }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

export default IntelligenceSection;
